"use client";

import { KeyboardEvent, useMemo, useRef } from "react";
import { cn } from "@/lib/utils";
import { NoxusFormLabel } from "@/components/Form/NoxusFormLabel";
import {
  COLOUR_TOKENS,
  ColourSurface,
  ColourTokenId,
  colourInitials,
} from "@/utils/colourTokens";

interface ColourPickerProps {
  value?: string;
  onChange: (id: ColourTokenId) => void;
  surface: ColourSurface;
  // Live text the swatches mirror — the name being typed into the sibling
  // field. Rendered as a monogram (2 letters for workspace, 1 otherwise).
  initialsSource?: string;
  label?: string;
  name?: string;
  disabled?: boolean;
}

export const ColourPicker = ({
  value,
  onChange,
  surface,
  initialsSource = "",
  label,
  name,
  disabled,
}: ColourPickerProps) => {
  const btnRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const selectedIndex = useMemo(() => {
    const i = COLOUR_TOKENS.findIndex((t) => t.id === value);
    return i === -1 ? 0 : i;
  }, [value]);

  const initials = colourInitials(initialsSource, surface);

  const move = (dir: 1 | -1) => {
    if (disabled) return;
    const next =
      (selectedIndex + dir + COLOUR_TOKENS.length) % COLOUR_TOKENS.length;
    onChange(COLOUR_TOKENS[next].id);
    btnRefs.current[next]?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      move(1);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      move(-1);
    }
  };

  return (
    <div>
      {label && <NoxusFormLabel label={label} name={name} />}
      <div
        role="radiogroup"
        aria-label={`${surface} colour`}
        aria-disabled={disabled}
        onKeyDown={handleKeyDown}
        className="flex items-center gap-3"
      >
        {COLOUR_TOKENS.map((token, i) => {
          const selected = i === selectedIndex;
          return (
            <button
              key={token.id}
              ref={(el) => {
                btnRefs.current[i] = el;
              }}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={token.name}
              tabIndex={selected ? 0 : -1}
              disabled={disabled}
              onClick={() => !disabled && onChange(token.id)}
              className={cn(
                "flex h-8 w-8 items-center justify-center rounded-[4px] border border-black/10 text-sm font-medium leading-none select-none transition-all outline-offset-2 focus-visible:outline-none dark:border-white/15",
                disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer",
                selected
                  ? "outline outline-[1.5px] outline-slate-900 dark:outline-slate-100"
                  : "outline-none hover:outline hover:outline-[1.5px] hover:outline-slate-300 dark:hover:outline-slate-600"
              )}
              style={{ backgroundColor: token.tint, color: token.text }}
            >
              {initials}
            </button>
          );
        })}
      </div>
    </div>
  );
};
